import {Injectable} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';
import {ProjectId} from '../../types/ids';
import {WebSocketService} from './websocket.service';
import {AuthService} from '../auth.service';
import {WmNotificationService} from '../../components/notification/wm-notification.service';
import {isUUID} from '../../utils/utils';
import {filter} from 'rxjs/operators';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import moment from 'moment';

export interface WebmateWebsocketEvent {
    eventType: string;
    projectId: ProjectId;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class EventsService {

    /* We try to get a total max retry time of about 1h */
    static readonly MAX_CONNECTION_RETRY_DELAY = 10000;
    static readonly MAX_AMOUNT_OF_CONNECTION_REQUESTS: number = 360;

    readonly FORCE_RELOAD_TIMEOUT_MIN: number = 1;
    readonly HAS_BEEN_RELOADED_STORAGE_KEY: string = 'event_service_has_been_reloaded';

    /* we use Subjects in this class, similar to the WebSocketService. Note that this corresponds to a PublishSubject in Rx, only RxJS calls this simply Subject.
     * A PublishSubject emits to an observer only those items that are emitted by the source Observable(s) subsequent to the time of the subscription. Note that
     * it may begin emitting items immediately upon creation (unless you have taken steps to prevent this), and so there is a risk that one or more items may be
     * lost between the time the Subject is created and the observer subscribes to it. We live with this, as we never want to repeat Events and a ReplaySubject
     * would to that (namely the last one at every new subscription, which would be terrible for any non-immutable Event such as 'Device.Added').*/
    private rawEventsCache?: Subject<MessageEvent>;
    private allEventsCache: Subject<WebmateWebsocketEvent> = new Subject<WebmateWebsocketEvent>();
    private activeSubscription: Subscription;
    private processedProjectEventsCache: { [s: string /* ProjectId */]: Subject<WebmateWebsocketEvent> } = {};
    private openProjects: ProjectId[] = [];

    /* we do not ask for WebSocket Connections (but do regularly check) if we are not authenticated. Since we do not want to drag a direct subscription of the
     * UserService into our WebSocket-Handling as that would only create problems, we simply ignore reconnect attempts if we are not authenticated.
     * NOTE: This also means there is potential for a race after Authenticating, aka someone asks us for Events but we have not yet received the new AuthData.
     * In this case, we will do one check-cycle before actually making a connection. Hence it is okay and we will get Events eventually. */
    private isAuthenticated: boolean = false;

    private amountOfFails: number = 0;
    private failureTime?: moment.Moment;

    constructor(private authService: AuthService, private webSocketService: WebSocketService, private notificationService: WmNotificationService, private persistenceService: PersistenceService) {
        this.authService.observeAuthStatus().subscribe(authStatus => {
            this.isAuthenticated = authStatus;
            this._reconnectWebsocket(this);
        });
        if (this.persistenceService.getItem(this.HAS_BEEN_RELOADED_STORAGE_KEY)) {
            this.notificationService.toastNotification('There was a connection outage. We automatically reloaded the page to make sure your view is up-to-date.', 'Connection restored');
            this.persistenceService.removeItem(this.HAS_BEEN_RELOADED_STORAGE_KEY);
        }
    }

    /** opens or reopens connections, connecting the WebSocket and notifying subscribers on a project basis */
    private _reconnectWebsocket(self: EventsService): void {
        if (self.rawEventsCache) {
            //eliminate the existing connection without triggering our reconnect handler
            self.activeSubscription.unsubscribe();
            self.rawEventsCache.complete();
            self.rawEventsCache = undefined;
        }
        if (self.isAuthenticated) {
            // This is consciously done in parallel to WebSocket reconnect as we are only interested in token refresh as a side effect.
            self.authService.probeAuthentication().subscribe();
            self.rawEventsCache = self.webSocketService.connectToEventsForProjects(self.openProjects);
            self.activeSubscription = self.rawEventsCache.subscribe(
                event => {
                    /** we basically build a weak subscription here, each time there is a new value (Event), we update the value of our Subject
                     * and thus inform all our Subscribers. This allows us to keep all subscriptions to the Object we give out intact no matter
                     * what happens to the WebSocket! */
                    if (event !== undefined && event.data !== undefined) {
                        if (self.amountOfFails > 0) {
                            if (self.failureTime && moment().diff(self.failureTime, 'minutes') >= self.FORCE_RELOAD_TIMEOUT_MIN) {
                                // > 10min => try to detect if current view is unsaved reload page and show notification afterwards
                                self.persistenceService.setItem(self.HAS_BEEN_RELOADED_STORAGE_KEY, 'true');
                                window.location.reload();
                            } else {
                                // < 10min => show toast with reload button
                                self.notificationService.toastDisconnectNotification();
                            }
                            self.failureTime = undefined;
                            self.amountOfFails = 0; //each time we get something, the connection is obviously okay so let's reset the doom-counter ;-)
                        }
                        // Ignore pong messages
                        if (event.data !== 'WMWebSocketPong' && event.data !== 'WMWebSocketTerminate') {
                            let msg = JSON.parse(event.data);
                            let projectId = msg['projectId'];
                            if (isUUID(projectId) && self.openProjects.indexOf(projectId) >= 0) {
                                self.processedProjectEventsCache[msg['projectId']].next(msg['ev'] as WebmateWebsocketEvent);
                            }
                            self.allEventsCache.next(msg['ev'] as WebmateWebsocketEvent);
                        }
                    } else {
                        console.debug('Event Service ignored broken Event: ', event);
                    }
                },
                error => {
                    console.error('Error in Event Service: ' + error);
                },
                (/* completed */) => {
                    if (!self.failureTime) {
                        self.failureTime = moment();
                    }
                    self.amountOfFails = self.amountOfFails + 1;
                    console.warn('Event Service lost WebSocket connection', self.amountOfFails);
                    self.notificationService.toastWarningNotification('Lost connection to webmate. Trying to reconnect...', 'Connection Issues');

                    if (self.amountOfFails <= EventsService.MAX_AMOUNT_OF_CONNECTION_REQUESTS) {
                        /** open a new Websocket Connection with exponential backoff. About 1h max */
                        setTimeout(self._reconnectWebsocket, Math.min((self.amountOfFails) * 1000, EventsService.MAX_CONNECTION_RETRY_DELAY), self);
                    } else {
                        self.notificationService.goToLoginNotification();
                    }
                }
            );
        }
    }

    public stopPullingEventsForProject(projectId: ProjectId): void {
        let index = this.openProjects.indexOf(projectId);
        if (index >= 0) {
            this.openProjects.splice(index, 1);
            this._reconnectWebsocket(this);
        }
    }

    public getEventsOfTypeForProject(projectId: ProjectId, type: string) {
        return this.getEventsForProject(projectId).pipe(filter(e => e['eventType'] === type));
    }

    public getEventsForProject(projectId: ProjectId): Observable<WebmateWebsocketEvent> {
        if (this.processedProjectEventsCache[projectId] === undefined) {
            /** we ever only create one "processed" Subject per project for the entire lifetime of this Service */
            this.processedProjectEventsCache[projectId] = new Subject<WebmateWebsocketEvent>();
            this.openProjects.push(projectId);
            this._reconnectWebsocket(this);
        }
        return this.processedProjectEventsCache[projectId].asObservable();
    }

    /**
     * Return event observables for any Event arriving (useful in services).
     */
    public getAllEvents(): Observable<WebmateWebsocketEvent> {
        return this.allEventsCache.asObservable();
    }

    /**
     * Return all events (in any project) of a given types.
     */
    public getAllEventsOfType(types: string[]): Observable<any> {
        return this.allEventsCache.pipe(
          filter((e: any) => types.includes(e['eventType']))
        );
    }

    /**
     * Return all events (in any project) of a given type.
     */
    public getAllEventsOfTypes(...types: string[]): Observable<WebmateWebsocketEvent> {
        return this.allEventsCache.pipe(filter(e => types.includes(e['eventType'])));
    }
}
